<template>
  <div class="h5-room-page">
    <template v-if="isRoomError"><error-404/></template>
    <template v-else>
      <header>
        <ul class="bread-crumb">
          <li>
            <a @click="jumpToHome">{{$t('common.home')}}</a>
          </li>
          <li>
            <a @click="handleOpenApp">{{roomInfo.title}}</a>
          </li>
        </ul>
      </header>
      <section class="live-cover" @click="handleOpenApp">
        <img :src="roomInfo.cover"/>
      </section>
      <section class="live-room-info">
        <div class="live-room-info__title">{{roomInfo.title}}</div>
        <div class="live-room-info__online">{{$t('roomInfo.viewer', roomInfo.online)}}</div>
      </section>
      <section class="streamer-info" @click="handleOpenApp">
        <div class="streamer-info__avatar">
          <img :src="roomInfo.primary.face"/>
        </div>
        <div>
          <div class="streamer-info__name">{{roomInfo.primary.name}}</div>
          <div class="streamer-info__stat">
            {{$t('userCard.followers', roomInfo.primary.followers)}} · {{$t('userCard.videos', roomInfo.primary.videos)}}
          </div>
        </div>
        <div class="streamer-info__follow">
          <template v-if="hasFollowed">
            <b-button
              type="default"
              class="user-card__btn disabled"
              :hollow="true"
            >
              {{$t('follow.following')}}
            </b-button>
          </template>
          <template v-else>
            <b-button 
              type="default"
              class="user-card__btn"
            >
              {{$t('follow.follow')}}
            </b-button>
          </template>
        </div>
      </section>
      <section class="open-app" @click="handleOpenApp">
        <i class="open-app__icon" data-v-405acb40=""></i>
        <span>App</span>
      </section>
      <section>
        <comment-container :roomId="roomId" :height="'auto'" v-if="isBrowser"/>
      </section>
      <footer class="comment-sender">
        <div class="comment-sender__input" @click="handleOpenApp">
          {{$t('comment.sender_input_placeholder')}}
        </div>
      </footer>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useLogin } from '@bilibili-intl/login';
import Error404 from '@/components/error/404.vue';
import CommentContainer from '@/components/comment/comment-container.vue';
import { useServerPrefetch } from '@/composables/use-server-prefetch';
import { DATA_ERROR } from '@/constants';
import { useI18n } from 'vue-i18n';
import { BButton } from '@bilibili-intl/components';
import { IAttentionState } from '@/service/room/types';
import { openApp, generateScheme } from '@/utils/open-app';
import LangManager from '@/locales/lang-manager';
import { useHostingEnv } from '@/common/env';

export default defineComponent({
  components: {
    CommentContainer,
    Error404,
    BButton
  },

  setup() {
    const store = useStore();
    const route = useRoute();
    const roomId = Number(route.params.roomId || 0);
    const locale = computed(() => LangManager.get());
    const { isBrowser } = useHostingEnv();

    const { t: $t } = useI18n();

    const roomInfo = computed(() => store.state.room.roomInfo);
    const isRoomError = computed(() => !roomInfo.value || roomInfo.value === DATA_ERROR);

    if (!roomId) {
      store.commit('setRoomInfo', DATA_ERROR);
      return {
        roomId,
        roomInfo,
        isRoomError,
      };
    }

    useServerPrefetch(async() => await store.dispatch('getRoomInfo', { roomId }), store.state.room.roomInfo);

    const loginContext = useLogin();

    const onLogin = () => {
      loginContext.openLogin();
    };

    onMounted(async () => {
      if (!roomId) {
        isRoomError.value = true;
        return;
      }

      try {
        await store.dispatch('getRoomInfo', { roomId });
      } catch (e) {
        console.log('[isRoomError]:', e);
        isRoomError.value = true;
      }
    });


    const hasFollowed = computed(
      () => roomInfo.value?.primary.attention !== IAttentionState.followed
    );


    const handleOpenApp = () => {
      openApp({
        onlyScheme: true,
        scheme: generateScheme(roomId)
      });
    };

    const jumpToHome = () => {
      window.location.href = `/${locale.value}`;
    };

    return {
      roomId,
      roomInfo,
      isRoomError,
      hasFollowed,
      isBrowser,
      onLogin,
      handleOpenApp,
      jumpToHome
    };
  },
});
</script>

<style lang="less" scoped>
@import '@/styles/var.less';
.h5-room-page {
  padding: 36px 8px 94px;
  background: #fff;

  .bread-crumb {
    padding: 20px 0 12.5px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    & > li {
      align-items: center;
      overflow: hidden;
      text-overflow: ellipsis;
      display: inline-block;
      padding-right: 10px;

      &:nth-child(1) {
        flex-shrink: 0;
        padding-right: 0;

        & > a {
          color: #333;
        }
      }

      & > a {
        font-weight: 700;
        font-size: 18px;
        line-height: 22px;
        white-space: nowrap;
        cursor: pointer;
        color: #666;
        vertical-align: middle;
      }

      & + li::before {
        content: ">";
        margin: 0 12px;
        line-height: 22px;
        font-weight: 700;
        font-size: 12px;
        vertical-align: middle
      }

    }     
    
  }

  .live-cover {
    margin-top: 8px;
    width: 100%;
    border-radius: 8px;
    overflow: hidden;

    & > img {
      width: 100%;
    }
  }
  .live-room-info {
    margin-top: 40px;

    &__title {
      font-size: 18px;
      line-height: 22px;
      font-weight: 700;
    }
    &__online {
      margin-top: 6px;
      font-size: 14px;
      color: #666;
      line-height: 18px;
    }
  }

  .streamer-info{
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 11px 4px 11px 8px;
    border-top: 1px solid hsla(0,0%,60%,.3);
    border-bottom: 1px solid hsla(0,0%,60%,.3);
    margin: 16px 0 12px;

    & > div:nth-child(2) {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      flex-grow: 1;
    }

    &__avatar {
      border-radius: 50%;
      margin-right: 8px;
      overflow: hidden;
      width: 36px;
      flex-shrink: 0;
      flex-grow: 0;

      & > img {
        width: 100%;
      }
    }

    &__name {
      font-size: 14px;
      line-height: 18px;
      font-weight: 500;
      margin-bottom: 4px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      margin-right: 24px;
    }
    &__stat {
      font-size: 12px;
      line-height: 12px;
      color: #666;
      margin-right: 24px;
    }

    &__follow {
      flex-shrink: 0;
      flex-grow: 0;
      justify-self: flex-end;
    }
  }

  .comment-sender {
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 94px;
    border: #ccc 1px solid;
    border-bottom: none;
    background: #fff;
    border-radius: 12px 12px 0 0;
    padding: 12px;

    &__input {
      height: 44px;
      line-height: 44px;
      padding-left: 8px;
      background: hsla(0,0%,60%,.1);
      border-radius: 4px;
      font-size: 14px;
      color: hsla(0,0%,60%,.5);
      cursor: pointer;
    }
  }
  .open-app {
    position: fixed;
    bottom: 212px;
    right: 16px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: #fff;
    border: 2px solid #333;
    font-size: 12px;
    color: #333;
    text-align: center;
    line-height: 12px;
    font-weight: 700;
    z-index: 100;
    cursor: pointer;

    &__icon {
      width: 12px;
      height: 12px;
      background-color: transparent;
      background-image: url('@/assets/icon_tv.svg');
      background-repeat: no-repeat;
      background-size: 100%;
    }
  }
}
</style>
<style lang="less">
.h5-room-page .error > img {
  height: auto !important;
}
</style>